import styled from '@emotion/styled'
import { Dimension } from '@pubstack/common/src/analytics/dimension'
import { MappedName, TopRevenuesByDim } from '@pubstack/common/src/analytics/query'
import { CurrencySymbol } from '@pubstack/common/src/currency'
import { FunctionComponent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAnalyticsQueryTanstack } from '~/api/analytics/analytics-query.api'
import Button from '~/components/Button'
import { normalizeAsTrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { useLocalStorage } from '~/hooks/useLocalStorage'
import { RevenueContainer } from '~/modules/analytics/common/components/topRevenueByDim/container'
import { PureTopRevenueByDim } from '~/modules/analytics/common/components/topRevenueByDim/PureTopRevenueByDim'
import {
  getIconFromDimension,
  getTitleFromDimension,
  getUriForMoreFromDimension,
  type OverviewRevenueChartProps,
} from '~/modules/analytics/common/components/topRevenueByDim/PureTopRevenueByDim.utils'
import { Context } from '~/state'
import { WithClassName } from '~/types/utils'

const OverviewRevenueChart: FunctionComponent<OverviewRevenueChartProps> = ({
  className,
  icon,
  title,
  context,
  dimensions,
  currencySymbol,
  dimension,
  revenueDisplayThreshold,
  moreUri,
  onRevenueChartSelect,
}) => {
  const analyticsQueryByTime = useAnalyticsQueryTanstack<TopRevenuesByDim>('top.revenues.by.dim', dimensions, context, { dimension })

  const revenueEmptyData: TopRevenuesByDim = { impressionCpmSum: [], adxImpressionCpmSum: [], openBiddingImpressionCpmSum: [], name: [], total: {} }
  const navigate = useNavigate()

  return (
    <PureTopRevenueByDim
      className={className}
      revenueDisplayThreshold={revenueDisplayThreshold}
      onRefreshClick={() => {
        analyticsQueryByTime.refetch()
      }}
      icon={icon}
      rawData={analyticsQueryByTime.data ?? revenueEmptyData}
      isLoading={analyticsQueryByTime.isFetching}
      error={analyticsQueryByTime.isError}
      empty={!analyticsQueryByTime.isFetching && !analyticsQueryByTime.data?.name.length}
      title={title}
      currencySymbol={currencySymbol}
      info={
        moreUri && (
          <Button variant={'tertiary'} onClick={() => navigate(moreUri)} trackingLabel={`ana-overview-${normalizeAsTrackingLabel(moreUri)}-navigate`}>
            More
          </Button>
        )
      }
      onValueSelect={onRevenueChartSelect}
    />
  )
}

type OverviewRevenueProps = WithClassName & {
  context: Context
  dimensions: Dimension[]
  currencySymbol: CurrencySymbol
  onRevenueChartSelect: (name: MappedName, dimension: Dimension) => void
}
const BaseOverviewRevenue: FunctionComponent<OverviewRevenueProps> = ({ className, context, dimensions, currencySymbol, onRevenueChartSelect }) => {
  const dimensionsToDisplay: Dimension[] = ['bidder', 'tagId', 'country', 'device', 'adUnit', 'size']
  const [showMinorData] = useLocalStorage({ key: 'showMinorData', defaultValue: false })
  const revenueDisplayThreshold = showMinorData ? Number.MIN_SAFE_INTEGER : 0.01

  return (
    <RevenueContainer className={className}>
      {dimensionsToDisplay.map((dimension, index) => (
        <OverviewRevenueChart
          key={index}
          title={getTitleFromDimension(dimension)}
          icon={getIconFromDimension(dimension)}
          revenueDisplayThreshold={revenueDisplayThreshold}
          dimension={dimension}
          context={context}
          dimensions={dimensions}
          currencySymbol={currencySymbol}
          moreUri={getUriForMoreFromDimension(dimension)}
          onRevenueChartSelect={(name) => onRevenueChartSelect && onRevenueChartSelect(name, dimension)}
        />
      ))}
    </RevenueContainer>
  )
}

export const OverviewRevenue = styled(BaseOverviewRevenue)``
